import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Confirmation for changing an AHU's Construction Class.
 *
 * The change is not just a label. Inline, as part of it:
 * `remove_unoffered_from_ahu` deletes every component and accessory the new
 * class does not offer from THIS unit, `clear_unoffered_fields` clears its
 * Configuration, Air System, Volume Control and Certifications,
 * `clear_unoffered_air_streams` clears all thirty air stream fields it can
 * carry, and `drop_special_options_outside_class` drops its special
 * options. Moving the class back brings none of it back -- so the user is
 * asked first.
 *
 * The wording is the product owner's, verbatim (2026-10-30). Two things it
 * says that the code deliberately does NOT do: the tunnel configuration is
 * never cleared, and a deleted Component leaves its SECTION in place as a
 * Plenum. Both were put to him on #2459.
 *
 * The CMS has the same warning on the other side of the same rule (unticking
 * an option there sweeps every AHU on the class); this is the per-unit half.
 *
 * ★ Esc is handled here. The CMS shell closes popups by clicking
 * `button[name="cancel"]`, but the board has no such shell -- its popups bind
 * their own key handler (`AccessoryMountingPopup`, `InsertSectionPopup`), so
 * this one does too.
 */
interface ChangeClassPopupProps {
  /** The class being moved to, as the user sees it. */
  name: string;
  onCancel: () => void;
  onApply: () => void;
}

const ChangeClassPopup = ({ name, onCancel, onApply }: ChangeClassPopupProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  /**
   * Line the dialog's LEFT edge up with the canvas toolbar's.
   *
   * ★ MEASURED, NOT A CSS OFFSET. `.popup-wrapper.width-small .popup-content`
   * puts the box at `calc(50% - 250px)`, which is a constant relative to
   * whatever the wrapper happens to be. Reading `#canvas_toolbar` instead
   * means the two edges line up by construction, at any viewport, and
   * nobody has to re-measure an offset when the data bar changes width.
   */
  useLayoutEffect(() => {
    const place = () => {
      const content = contentRef.current;
      const toolbar = document.getElementById('canvas_toolbar');
      if (!content || !toolbar) return;
      // ★ NO ENTRY ANIMATION ON THIS ONE, and that is a measurement
      // requirement rather than a style choice. `easePopup` scales the box
      // from 0 to 1 over 0.3s, and `getBoundingClientRect` reports the
      // SCALED box -- so a position computed while it runs lands wherever
      // the animation happened to be at that instant. The first attempt
      // overshot by 109px for exactly that reason. Measuring after the
      // animation instead would fix the number but show the box jumping
      // into place at the end, which is worse than not scaling at all.
      content.style.setProperty('animation', 'none', 'important');
      // ★ CORRECT BY THE GAP, don't compute an absolute position. Working
      // out `left` from a containing block means knowing which ancestor
      // that is, and here it is not the one `offsetParent` reports -- the
      // first attempt landed 81px short for exactly that reason. Measuring
      // the gap between the two right edges and shifting `left` by it needs
      // to know nothing about the cascade, and lands on the edge whatever
      // the containing block turns out to be.
      // ★ THE BOARD IS SCALED, so the gap and `left` are in different
      // units. An ancestor of the forms panel carries a transform of about
      // 0.82, which `getBoundingClientRect` reflects and `offsetWidth` does
      // not -- a 390px dialog measures 320px on screen. `left` is applied
      // in the element's own unscaled pixels, so shifting it by a gap read
      // in screen pixels only ever closes 82% of the distance: it converged
      // 109 -> 22 -> 4px, one pass at a time, and never arrived. Dividing
      // the gap by the scale lands it in one.
      const box = content.getBoundingClientRect();
      const scale = content.offsetWidth > 0 ? box.width / content.offsetWidth : 1;
      if (scale <= 0) return;
      const style = window.getComputedStyle(content);

      // left edge on the canvas toolbar's
      const gap = toolbar.getBoundingClientRect().left - box.left;
      if (Math.abs(gap) >= 1) {
        const current = parseFloat(style.left) || 0;
        content.style.setProperty(
          'left', `${Math.round(current + gap / scale)}px`, 'important');
      }

      // ...and centred down the page. Measured the same way rather than
      // written as `calc(50% - <half the height>)`: the height depends on
      // how the copy wraps, which depends on the width, so a constant here
      // would be wrong the moment the text changed. The CSS it overrides
      // is `top: 20vh`.
      const drop = (window.innerHeight - box.height) / 2 - box.top;
      if (Math.abs(drop) >= 1) {
        const currentTop = parseFloat(style.top) || 0;
        content.style.setProperty(
          'top', `${Math.round(currentTop + drop / scale)}px`, 'important');
      }
    };
    // ★ AND AGAIN ON THE NEXT FRAME. The first pass runs before the
    // browser has finished laying the dialog out, so its box can still
    // change under us -- measured 22px short that way. `place` corrects by
    // the remaining gap and returns early once it is under a pixel, so a
    // second pass is either the fix or a no-op, never a wobble.
    place();
    const frame = window.requestAnimationFrame(place);
    window.addEventListener('resize', place);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', place);
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  /**
   * ★ RENDERED ON <body>, NOT WHERE IT IS DECLARED.
   *
   * In place it mounts inside `.forms-container.active`, and that costs it
   * three ways at once, all of which this fixes:
   *
   * * the backdrop could not cover the page. `.forms-container.active >
   *   div { width: 520px }` matches the WRAPPER, so the overlay was sized
   *   like a form panel -- 426px of grey over the data bar and nothing
   *   anywhere else (asked for 2026-10-30).
   * * the wrapper is `overflow: auto`, so a dialog placed at the canvas
   *   toolbar's edge fell outside it and was CLIPPED AWAY. The pop-up
   *   vanished for exactly that reason, and was being held on screen by an
   *   `overflow: visible` override that is no longer needed.
   * * the board subtree is scaled ~0.82, so the dialog was rendered at 82%
   *   and every position written to it had to be divided by that scale.
   *
   * On `<body>` the wrapper is a plain fixed, full-viewport overlay and the
   * dialog is drawn at its own size. The measuring below is unchanged and
   * still correct -- it works in screen coordinates either way, and the
   * scale it computes is simply 1 now.
   */
  return createPortal(
    <div className="popup-wrapper width-small active">
      <div className="popup-content" ref={contentRef}>
        <h2 className="popup-heading">Change the Construction Class?</h2>
        <div className="popup-content-part">
          <p>
            For All Active and Editable (Non-Static) Air Handling Units, selected
            Settings, Configuration, Air System, Air Stream, Certification, Volume
            Control, Components and Accessories which the new Construction Class does
            not offer will be deleted and removed from this Air Handling Unit
            permanently, if the new Construction Class does not offer them.
          </p>
          <p>
            Returning to the original Construction Class will not bring any of the
            above back.
          </p>
          <span className="model-name">New Construction Class : {name}</span>
        </div>
        <div className="popup-control-buttons">
          <button className="btn right not-highlighted-btn" name="apply" onClick={onApply}>
            Apply
          </button>
          <button className="btn right highlighted-btn" name="cancel" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ChangeClassPopup;
